import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

interface OptionSheetProps {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
  title: string;
  acceptLabel?: string;
  acceptDisabled?: boolean;
  /** When false, clicking accept will NOT auto-close the sheet (for async operations). Default true. */
  closeOnAccept?: boolean;
  children: React.ReactNode;
}

const OptionSheet: React.FC<OptionSheetProps> = ({
  open,
  onClose,
  onAccept,
  title,
  acceptLabel = 'Aceptar',
  acceptDisabled = false,
  closeOnAccept = true,
  children,
}) => {
  const [animated, setAnimated] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    }
  }, [open]);

  const handleClose = () => {
    setAnimated(false);
    setTimeout(() => onClose(), 300);
  };

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    }
    if (closeOnAccept) {
      setAnimated(false);
      setTimeout(() => onClose(), 300);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - dragStartY.current;
    if (deltaY > 0) setDragY(deltaY);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) {
      handleClose();
    }
    setDragY(0);
    dragStartY.current = null;
  };

  if (!open) return null;

  return (
    <>
      {/* Desktop backdrop overlay */}
      <div
        className={`hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer transition-opacity duration-300 ${animated ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      {/* Desktop Side Panel */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">{title}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <div className="px-5 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={acceptDisabled}
            className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
              !acceptDisabled
                ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
            }`}
          >
            {acceptLabel}
          </button>
        </div>
      </div>

      {/* Mobile Slide - rendered via portal */}
      {ReactDOM.createPortal(
        <>
          <div
            className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${
              animated ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={handleClose}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
            style={{
              height: 'calc(100dvh - 1rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform:
                dragY > 0
                  ? `translateY(${dragY}px)`
                  : animated
                    ? 'translateY(0)'
                    : 'translateY(100%)',
              transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            <div
              className="py-2 cursor-grab active:cursor-grabbing"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            <div className="flex items-center justify-between px-4 pb-3">
              <button
                type="button"
                onClick={handleClose}
                className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
              >
                Cancelar
              </button>
              <h3 className="text-[15px] font-semibold text-brand-black/90">{title}</h3>
              <button
                type="button"
                onClick={handleAccept}
                disabled={acceptDisabled}
                className={`text-[15px] font-semibold w-20 text-right transition-opacity ${
                  !acceptDisabled
                    ? 'text-[#1644EB] active:opacity-50'
                    : 'text-[#3C3C43]/20 cursor-not-allowed'
                }`}
              >
                {acceptLabel}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

export default OptionSheet;
